<template>
  <section>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="4">
          <e-store-combo
            id="price-suggestion-log-filter-store"
            v-model="filters.storeId"
            :required="false"
          />
        </b-col>
        <b-col md="4">
          <e-search-sku
            id="price-suggestion-log-filter-sku"
            v-model="filters.skuId"
            :required="false"
            :include-fixed-assets="true"
          />
        </b-col>
        <b-col md="4">
          <FormulateInput
            id="price-suggestion-log-filter-status"
            v-model="filters.status"
            type="vue-select"
            :label="$t('Status')"
            :placeholder="$t('Todos')"
            :options="statusOptions"
          />
        </b-col>
        <b-col md="4">
          <FormulateInput
            id="price-suggestion-log-filter-transaction"
            v-model="filters.transactionId"
            type="text"
            :label="$t('Transação')"
          />
        </b-col>
        <b-col md="4">
          <FormulateInput
            id="price-suggestion-log-filter-period"
            v-model="period"
            type="date-range-picker"
            :label="$t('Data/Hora')"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        id="price-suggestion-log-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('PRICE_SUGGESTION_LOG.NAME'), 'male', queryExecuted)"
        :fields="fields"
        :items="logs"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(storeId)="row">
          {{ getStoreName(row.item.storeId) }}
        </template>

        <template #cell(status)="row">
          {{ getStatusLabel(row.item.status) }}
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="price-suggestion-log-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>
  </section>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { EPageSizeSelector, EPaginationSummary, EFilters } from '@/views/components'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import ESearchSku from '@/views/components/inputs/ESearchSku.vue'
import { priceSuggestionDomains } from '@/mixins'

export default {
  components: {
    BRow,
    BCol,
    BPagination,
    BTable,
    BCardActions,
    EPaginationSummary,
    EPageSizeSelector,
    EFilters,
    EStoreCombo,
    ESearchSku,
  },

  mixins: [priceSuggestionDomains],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/catalog/priceSuggestionLogs', ['filters', 'sorting', 'logs', 'paging']),
    ...mapGetters('app', ['allStoreOptions']),

    statusOptions() {
      return this.skuPriceSuggestionStatusOptions()
    },

    // O `date-range-picker` opera sobre um objeto { startDate, endDate }; mapeamos
    // esse modelo para os campos planos createdDateFrom/createdDateTo do filtro.
    period: {
      get() {
        return {
          startDate: this.filters.createdDateFrom,
          endDate: this.filters.createdDateTo,
        }
      },
      set(value) {
        this.filters.createdDateFrom = value?.startDate || null
        this.filters.createdDateTo = value?.endDate || null
      },
    },

    fields() {
      const currency = val => (val === null || val === undefined ? '—' : this.$options.filters.currency(val))

      return [
        {
          label: this.$t('Índice'),
          key: 'itemIndex',
          class: 'text-center',
          thStyle: { width: '90px' },
          sortable: false,
        },
        {
          label: this.$t('Loja'),
          key: 'storeId',
          thClass: 'text-center',
          tdClass: 'text-left',
          thStyle: { width: '220px' },
          sortable: true,
        },
        {
          label: this.$t('SKU'),
          key: 'skuId',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
          formatter: val => (val === null || val === undefined ? '—' : val),
        },
        {
          label: this.$t('Preço Sugerido'),
          key: 'suggestedPrice',
          class: 'text-right',
          sortable: false,
          formatter: currency,
        },
        {
          label: this.$t('Preço Vigente'),
          key: 'currentPrice',
          class: 'text-right',
          sortable: false,
          formatter: currency,
        },
        {
          label: this.$t('Custo Utilizado'),
          key: 'costUsed',
          class: 'text-right',
          sortable: false,
          formatter: currency,
        },
        {
          label: this.$t('Preço Mínimo'),
          key: 'minimumAllowedPrice',
          class: 'text-right',
          sortable: false,
          formatter: currency,
        },
        {
          label: this.$t('Preço Aplicado'),
          key: 'appliedPrice',
          class: 'text-right',
          sortable: false,
          formatter: currency,
        },
        {
          label: this.$t('Variação (%)'),
          key: 'variationPercent',
          class: 'text-right',
          sortable: false,
          formatter: val =>
            val === null || val === undefined ? '—' : this.$options.filters.number(val, 2),
        },
        {
          label: this.$t('Status'),
          key: 'status',
          thClass: 'text-center',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Motivo'),
          key: 'reason',
          thClass: 'text-center',
          tdClass: 'text-left',
          sortable: false,
          formatter: val => val || '—',
        },
        {
          label: this.$t('Transação'),
          key: 'transactionId',
          thClass: 'text-center',
          tdClass: 'text-left',
          sortable: false,
          formatter: val => val || '—',
        },
        {
          label: this.$t('Data/Hora'),
          key: 'createdDate',
          class: 'text-center',
          thStyle: { width: '160px' },
          sortable: true,
          formatter: val => (val ? this.$options.filters.datetime(val) : '—'),
        },
      ]
    },
  },

  mounted() {
    // Ordenação padrão por timestamp (CreatedDate) decrescente e página com 50 registros.
    this.setSortBy('createdDate')
    this.setSortDesc(true)
    this.setPageSize(50)
    this.getData()
  },

  methods: {
    ...mapActions('pages/catalog/priceSuggestionLogs', [
      'fetchLogs',
      'setCurrentPage',
      'setPageSize',
      'setSortBy',
      'setSortDesc',
      'resetFilters',
    ]),

    getStoreName(storeId) {
      if (storeId === null || storeId === undefined) return '—'
      const store = this.allStoreOptions?.find(s => s.value === storeId)
      return store ? store.label : storeId
    },

    getStatusLabel(status) {
      return this.getSkuPriceSuggestionStatusLabel(status)
    },

    // Valida o intervalo antes de consultar: a data final não pode ser anterior à inicial.
    isValidDateRange() {
      const { createdDateFrom, createdDateTo } = this.filters
      if (createdDateFrom && createdDateTo) {
        return new Date(createdDateTo) >= new Date(createdDateFrom)
      }
      return true
    },

    async getData() {
      if (!this.isValidDateRange()) {
        this.showInvalidDataMessage()
        return
      }

      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchLogs()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },
  },
}
</script>

<style></style>
